import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import { submitAOBFStep2 } from '../../services/advisor_services';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import RatingCard from './RatingCard';

const useStyles = (isSM) => ({
  main: {
    with: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    padding: isSM ? '16px 8px' : '16px 120px 0',
    justifyContent: 'center',
    width: '100%',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: { xxs: '32px', xs: '24px' },
    letterSpacing: '-0.4300000071525574px',
    marginBottom: '16px',
    padding: { xxs: '0px 8px', xs: '0px' },
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    maxWidth: '360px',
    textAlign: 'center',
  },
  info: {
    fontStyle: 'italic',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '16px',
  },
  gridContainerProps: {
    container: true,
    spacing: 0,
    justifyContent: 'center',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: '#C7D0DE',
      },
    },
  },
});

function AOBFStep2() {
  const { accountDetails } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [profession, setProfession] = useState(
    accountDetails.aobfPrimaryExpertise || ''
  );
  const [additionalSkills1, setAdditionalSkills1] = useState();
  const [additionalSkills2, setAdditionalSkills2] = useState();
  const [additionalSkills3, setAdditionalSkills3] = useState();

  const professionOptions = [
    'Founder or CEO',
    'Marketing',
    'Business Professional',
    'Operations',
    'Technology',
    'Product',
    'Finance',
    'Sales',
    'Human Resources',
    'Legal',
    'Other',
  ];

  const disableButton = () => {
    if (isLoading || !profession) {
      return true;
    }
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      let aobfAdditionalExpertise = [];
      if (additionalSkills1) {
        aobfAdditionalExpertise.push(additionalSkills1);
      }
      if (additionalSkills2) {
        aobfAdditionalExpertise.push(additionalSkills2);
      }
      if (additionalSkills3) {
        aobfAdditionalExpertise.push(additionalSkills3);
      }
      await submitAOBFStep2({
        id: accountDetails.id,
        aobfAdditionalExpertise: aobfAdditionalExpertise.join(', '),
        aobfPrimaryExpertise: profession,
      });
      history.push('/advisor/aobf/step3');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
      console.error(error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Profile Builder - AdvisoryCloud</title>
      </Helmet>

      <Box sx={classes.main}>
        <Box sx={classes.header}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={isSM ? 240 : 180}
            height={isSM ? 80 : 60}
            cursor="default"
          />
        </Box>

        <Box sx={classes.body}>
          <Typography sx={classes.title}>
            Help Us Match You to Boards
          </Typography>
          <Typography sx={{ ...classes.subtitle, maxWidth: '380px' }}>
            AdvisoryCloud makes it simple for businesses of all sizes to build
            their advisory boards. Businesses want larger boards and new
            perspectives (different ages, backgrounds and types of experience).
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>
                  Primary Area of Expertise:
                </Typography>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <Select
                  fullWidth
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  displayEmpty
                  sx={{ marginBottom: '8px', borderRadius: '10px' }}
                  id="aobf-profession"
                >
                  <MenuItem value="" disabled>
                    Select one
                  </MenuItem>
                  {professionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>
                  Additional Areas of Expertise (optional):
                </Typography>
                <Typography sx={classes.info}>
                  Add 3 additional skills, industries, or areas of expertise
                </Typography>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <TextField
                  fullWidth
                  placeholder="ex. Software, AI..."
                  value={additionalSkills1}
                  onChange={(e) => setAdditionalSkills1(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  sx={classes.textField}
                  id="aobf-additional-skills"
                />
              </Grid>
              <Grid {...classes.gridItemProps}>
                <TextField
                  fullWidth
                  placeholder="ex. B2B, Account Based Marketing..."
                  value={additionalSkills2}
                  onChange={(e) => setAdditionalSkills2(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  sx={classes.textField}
                  id="aobf-additional-skills"
                />
              </Grid>
              <Grid {...classes.gridItemProps}>
                <TextField
                  fullWidth
                  placeholder="ex. 50-something demographic"
                  value={additionalSkills3}
                  onChange={(e) => setAdditionalSkills3(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  sx={classes.textField}
                  id="aobf-additional-skills"
                />
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={disableButton()}
                  id="aobf-submit-button"
                  sx={{
                    borderRadius: '50px',
                    backgroundColor: '#3171F6',
                    '&:hover': { backgroundColor: '#3171F6' },
                    height: '48px',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 700,
                    mb: '16px',
                    mt: '8px',
                  }}
                >
                  {isLoading ? <LoadingLogoSkeleton /> : 'Last Step'}
                </Button>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <RatingCard
                  quote="I have been successfully matched by AdvisoryCloud with two companies that would never have been on my radar."
                  name="Michael Harris"
                  title="CEO, Michael Harris Group"
                />
              </Grid>
            </Grid>
            <ACSnackbar
              onClose={() => setOpenSnackbar(false)}
              open={openSnackbar}
              severity="error"
              text="Something went wrong"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default AOBFStep2;
